// ============================================================================
// Deactivate-subcontractor modal (AFB-0028, Stage 4 2026-06-09).
//
// "Delete" = soft-deactivate via provider_status — block_delete forbids hard
// DELETE on service_providers. Flow:
//   1. Dependency check (client reads, RLS-safe for hr/superuser): Active
//      service_assignments → facilities (+AM via v_am_name) → accounts, plus
//      open sub_replacement_requests referencing the provider.
//   2. Dependents exist → warning modal listing each facility/account by name
//      with worker + AM context. No dependents → simple confirm.
//   3. Proceed → deactivate_service_provider RPC (SECURITY DEFINER, hr/
//      superuser gate): sets Inactive + mints one High facility_tasks row per
//      orphaned facility, assigned to that facility's AM
//      (facilities.account_manager_eid — NOT accounts). task_id omitted —
//      trigger-assigned. Assignments are NOT ended here; sub replacement owns
//      cutover.
// ============================================================================

import { useEffect, useState } from 'react';
import { AlertTriangle, Loader2, X } from 'lucide-react';
import { supabase } from '../../shared/supabase';
import { useAuth } from '../../shared/auth';
import { Button, useToast } from '../../shared/components';
import { fetchAmNameMap } from '../../shared/am-name';
import { overlayClickClose } from '../../shared/overlay-close';
import { errorMessage } from '../../shared/errors';

interface DependentFacility {
  facility_id: string;
  facility_name: string | null;
  account_name: string | null;
  worker_name: string | null;
  am_name: string | null;
}

interface Props {
  providerId: string;
  providerName: string | null;
  onClose: () => void;
  /** Called after successful deactivation so the caller can refetch. */
  onDone: () => void;
}

export function DeactivateSubModal({ providerId, providerName, onClose, onDone }: Props) {
  const toast = useToast();
  const { employee } = useAuth();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [dependents, setDependents] = useState<DependentFacility[]>([]);
  const [openRequests, setOpenRequests] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError(null);
      try {
        // Active assignments for this provider (flat fetch + client join).
        const { data: sas, error: e1 } = await supabase
          .from('service_assignments')
          .select('assignment_id, facility_id, worker_name')
          .eq('provider_id', providerId)
          .eq('assignment_status', 'Active');
        if (e1) throw e1;
        const saList = (sas ?? []) as Array<{
          assignment_id: string;
          facility_id: string | null;
          worker_name: string | null;
        }>;

        const facIds = [...new Set(saList.map((s) => s.facility_id).filter((v): v is string => Boolean(v)))];
        const facRes = facIds.length
          ? await supabase
              .from('facilities')
              .select('facility_id, facility_name, account_id, account_manager_eid')
              .in('facility_id', facIds)
          : { data: [], error: null };
        if (facRes.error) throw facRes.error;
        const facs = (facRes.data ?? []) as Array<{
          facility_id: string;
          facility_name: string | null;
          account_id: string | null;
          account_manager_eid: string | null;
        }>;

        const acctIds = [...new Set(facs.map((f) => f.account_id).filter((v): v is string => Boolean(v)))];
        const acctRes = acctIds.length
          ? await supabase.from('accounts').select('account_id, account_name').in('account_id', acctIds)
          : { data: [], error: null };
        const acctMap: Record<string, string | null> = {};
        for (const a of (acctRes.data ?? []) as Array<{ account_id: string; account_name: string | null }>) {
          acctMap[a.account_id] = a.account_name;
        }

        // AM names via the canonical v_am_name resolver (employees RLS-safe).
        const amMap = await fetchAmNameMap(facs.map((f) => f.account_manager_eid));

        const workerByFac: Record<string, string | null> = {};
        for (const s of saList) {
          if (s.facility_id && s.worker_name && !workerByFac[s.facility_id]) {
            workerByFac[s.facility_id] = s.worker_name;
          }
        }

        const deps: DependentFacility[] = facs.map((f) => ({
          facility_id: f.facility_id,
          facility_name: f.facility_name,
          account_name: f.account_id ? acctMap[f.account_id] ?? null : null,
          worker_name: workerByFac[f.facility_id] ?? null,
          am_name: f.account_manager_eid ? amMap.get(f.account_manager_eid) ?? null : null,
        }));

        // Open replacement requests touching this provider (incoming intent or
        // outgoing via one of its active assignments).
        const saIds = saList.map((s) => s.assignment_id);
        const orFilter = saIds.length
          ? `incoming_provider_id.eq.${providerId},outgoing_assignment_id.in.(${saIds.join(',')})`
          : `incoming_provider_id.eq.${providerId}`;
        const { data: reqs } = await supabase
          .from('sub_replacement_requests')
          .select('request_id, status')
          .or(orFilter)
          .not('status', 'in', '(Rejected,Cancelled,Completed)')
          .is('deleted_at', null);
        if (cancelled) return;
        setDependents(deps);
        setOpenRequests(((reqs ?? []) as Array<{ request_id: string }>).map((r) => r.request_id));
      } catch (e) {
        if (!cancelled) setLoadError(errorMessage(e, 'Could not load dependencies.'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [providerId]);

  async function confirm() {
    setBusy(true);
    setError(null);
    try {
      const { data, error: e } = await supabase.rpc('deactivate_service_provider', {
        p_provider_id: providerId,
        p_created_by_eid: employee?.employee_id ?? null,
      });
      if (e) throw e;
      const res = data as { tasks_created?: number } | null;
      const n = res?.tasks_created ?? 0;
      toast.success(`${providerName ?? providerId} deactivated`, {
        description:
          n > 0
            ? `${n} reassignment to-do${n === 1 ? '' : 's'} created for the affected facilities' AMs.`
            : 'No active facilities — no follow-up tasks needed.',
      });
      onDone();
      onClose();
    } catch (e) {
      setError(errorMessage(e, 'Deactivation failed.'));
    } finally {
      setBusy(false);
    }
  }

  const hasDeps = dependents.length > 0 || openRequests.length > 0;

  return (
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center bg-tcs-scrim/60 p-4"
      role="dialog"
      aria-modal="true"
      {...overlayClickClose(onClose)}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="max-h-[85vh] w-full max-w-lg overflow-y-auto rounded-lg bg-tcs-overlay p-5 shadow-xl"
      >
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-tcs-text">
            Deactivate {providerName ?? providerId}?
          </h2>
          <button type="button" onClick={onClose} aria-label="Close" className="rounded p-1 hover:bg-tcs-surface">
            <X className="h-4 w-4" />
          </button>
        </div>
        <p className="mt-1 text-xs text-tcs-text-muted">
          Subs are never hard-deleted — this sets the provider Inactive and keeps
          history intact. Active assignments stay until a replacement is cut over.
        </p>

        {loading && (
          <p className="mt-4 text-sm text-tcs-text-muted">
            <Loader2 className="mr-1 inline h-4 w-4 animate-spin" />
            Checking active facilities and open requests…
          </p>
        )}
        {loadError && (
          <div className="mt-3 rounded-md border border-rag-red bg-st-danger-bg p-2 text-sm text-st-danger-fg-strong">{loadError}</div>
        )}

        {!loading && !loadError && hasDeps && (
          <div className="mt-4 rounded-md border border-st-warn-border-strong bg-st-warn-bg p-3">
            <div className="flex items-center gap-2 text-sm font-medium text-st-warn-fg-strong">
              <AlertTriangle className="h-4 w-4" />
              This sub still covers {dependents.length} facilit{dependents.length === 1 ? 'y' : 'ies'}
            </div>
            <ul className="mt-2 space-y-1.5">
              {dependents.map((d) => (
                <li key={d.facility_id} className="text-xs text-st-warn-fg-strong">
                  <span className="font-medium">{d.facility_name ?? d.facility_id}</span>
                  {d.account_name ? ` — ${d.account_name}` : ''}
                  {d.worker_name ? ` · cleaner: ${d.worker_name}` : ''}
                  {d.am_name ? ` · AM: ${d.am_name}` : ''}
                </li>
              ))}
            </ul>
            {openRequests.length > 0 && (
              <p className="mt-2 text-xs text-st-warn-fg-strong">
                Open replacement request{openRequests.length === 1 ? '' : 's'}:{' '}
                <span className="font-mono">{openRequests.join(', ')}</span>
              </p>
            )}
            <p className="mt-2 text-xs text-st-warn-fg-strong">
              Proceeding creates one <strong>High-priority “assign new subcontractor”
              to-do per facility</strong>, assigned to that facility&rsquo;s AM.
            </p>
          </div>
        )}

        {!loading && !loadError && !hasDeps && (
          <p className="mt-4 rounded-md border border-tcs-border bg-tcs-bg-muted/40 p-3 text-sm text-tcs-text-secondary">
            No active facilities or open replacement requests reference this sub.
            Safe to deactivate.
          </p>
        )}

        {error && (
          <div className="mt-3 rounded-md border border-rag-red bg-st-danger-bg p-2 text-sm text-st-danger-fg-strong">{error}</div>
        )}

        <div className="mt-5 flex items-center justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button variant="destructive" size="sm" onClick={confirm} disabled={busy || loading || Boolean(loadError)}>
            {busy ? 'Deactivating…' : hasDeps ? 'Deactivate & create to-dos' : 'Deactivate'}
          </Button>
        </div>
      </div>
    </div>
  );
}
